"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Mail, Clock, ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu, MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
function FooterMenuRecursiveList({
  items,
  depth = 0
}: {
  items: MenuItem[];
  depth?: number;
}) {
  if (!items || items.length === 0) return null;
  return <ul className={depth === 0 ? "flex flex-col gap-2.5" : "flex flex-col gap-1.5 pl-3 border-l border-border/40 mt-1.5"}>
      {items.map((item, i) => <li key={item.id} data-index={i} className="text-sm">
          <Link to={item.href || "#"} className="text-foreground/80 hover:text-foreground transition-colors inline-flex items-center gap-1 group font-default">
            <span>{item.label}</span>
            {item.children && item.children.length > 0 ? <span className="text-xs text-muted-foreground group-hover:text-foreground transition-colors">/</span> : null}
          </Link>
          {item.children && item.children.length > 0 && <FooterMenuRecursiveList items={item.children} depth={depth + 1} />}
        </li>)}
    </ul>;
}
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-2">
        <div className="h-4 w-28 bg-muted animate-pulse rounded-xs" />
        <div className="h-4 w-36 bg-muted animate-pulse rounded-xs" />
        <div className="h-4 w-24 bg-muted animate-pulse rounded-xs" />
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    return <ul className="flex flex-col gap-2.5">
        <li data-index={0} className="text-sm">
          <Link to="/HomePage" className="text-foreground/80 hover:text-foreground transition-colors font-default">
            Home
          </Link>
        </li>
        <li data-index={1} className="text-sm">
          <Link to="/AnchorOrbitMethodPage" className="text-foreground/80 hover:text-foreground transition-colors font-default">
            The Anchor &amp; Orbit Method
          </Link>
        </li>
        <li data-index={2} className="text-sm">
          <Link to="/OrnateCurationServicesLegacyLensPage" className="text-foreground/80 hover:text-foreground transition-colors font-default">
            Ornate Curation Services &amp; The Legacy Lens
          </Link>
        </li>
        <li data-index={3} className="text-sm">
          <Link to="/LegacyExpeditionSuitePage" className="text-foreground/80 hover:text-foreground transition-colors font-default">
            The Legacy Expedition Suite
          </Link>
        </li>
        <li data-index={4} className="text-sm">
          <Link to="/GroupAffinityTravelPage" className="text-foreground/80 hover:text-foreground transition-colors font-default">
            Group &amp; Affinity Travel
          </Link>
        </li>
        <li data-index={5} className="text-sm">
          <Link to="/AboutDiscoveryConsultationPage" className="text-foreground/80 hover:text-foreground transition-colors font-default">
            About &amp; Discovery Consultation
          </Link>
        </li>
      </ul>;
  }
  return <FooterMenuRecursiveList items={menuItems} />;
}
export default function Footer() {
  const shouldReduceMotion = useReducedMotion();
  const currentYear = new Date().getFullYear();
  const socialLinks = [{
    label: "Instagram",
    to: "https://instagram.com/eleetetraveldesign"
  }, {
    label: "LinkedIn",
    to: "https://linkedin.com/company/e-leete-travel-design"
  }];
  return <section id="footer" data-nav="light" className="w-full bg-background border-t border-border text-foreground font-default">
      <div className="mx-auto max-w-[1320px] px-6 sm:px-8 lg:px-12 py-16 sm:py-20 lg:py-24">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7,
        ease: [0.16, 1, 0.3, 1]
      }} className="grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column: Brand Identity and Legal */}
          <div className="md:col-span-4 lg:col-span-5 flex flex-col items-start gap-6">
            <Link to="/" className="inline-block outline-hidden focus-visible:ring-2 focus-visible:ring-ring rounded-xs">
              <WvcLogo className="h-14 w-auto text-foreground" />
            </Link>
            <p className="text-sm leading-relaxed text-muted-foreground max-w-sm">
              Mediator and logistical architect of milestone travel for multi-generational families. Built on family legacy, generational harmony, and logistical precision.
            </p>
            <div className="pt-2 text-xs text-muted-foreground/80">
              <p>© {currentYear} E•Leete Travel Design. All rights reserved.</p>
              <p className="mt-1">Under the parent E•Leete Group.</p>
            </div>
          </div>

          {/* Center Column: Secondary Navigation */}
          <div className="md:col-span-4 lg:col-span-3 flex flex-col gap-4">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Navigation
            </h3>
            <MenuProvider menu_id="17">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          {/* Right Column: Contact & Socials */}
          <div className="md:col-span-4 lg:col-span-4 flex flex-col gap-6">
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">
                Studio Inquiries
              </h3>
              <ul className="flex flex-col gap-3 text-sm">
                <li data-index={0} className="flex items-center gap-2.5 text-foreground/90">
                  <Mail className="size-4 text-muted-foreground shrink-0" />
                  <a href="mailto:info@e-leetetraveldesign.com" className="hover:text-primary transition-colors underline-offset-4 hover:underline font-default">
                    info@e-leetetraveldesign.com
                  </a>
                </li>
                <li data-index={1} className="flex items-center gap-2.5 text-muted-foreground">
                  <Clock className="size-4 text-muted-foreground shrink-0" />
                  <span>By appointment only</span>
                </li>
              </ul>
            </div>

            <div className="pt-2 border-t border-border/40">
              <h4 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">
                Connect
              </h4>
              <div className="flex items-center gap-4">
                {socialLinks.map((social, i) => <Link key={social.label} data-index={i} to={social.to} newTab className="text-sm text-foreground/80 hover:text-foreground inline-flex items-center gap-1 group transition-colors">
                    <span>{social.label}</span>
                    <ArrowUpRight className="size-3 text-muted-foreground group-hover:text-foreground group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </Link>)}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}